/**
 * draftStore.ts
 * Generic, reusable in-memory draft store.
 *
 * Many forms (signup, login, and any future multi-field flow) need to keep the
 * user's typed data alive across navigation — without writing it to disk. A
 * module-level value does exactly that: it lives for the session/tab and is
 * dropped the moment `clear()` is called, so sensitive data (e.g. passwords) is
 * never persisted.
 *
 * This factory removes the copy-paste get/set/clear boilerplate that each form
 * used to duplicate. Create one typed store per form and reuse it anywhere:
 *
 *   // utils or screen module
 *   export interface LoginDraft { email: string; password: string; }
 *   const store = createDraftStore<LoginDraft>();
 *   export const { get: getLoginDraft, set: setLoginDraft, clear: clearLoginDraft } = store;
 *
 *   // in a component
 *   const draft = getLoginDraft();        // LoginDraft | null
 *   setLoginDraft({ email, password });   // persist across navigation
 *   clearLoginDraft();                    // drop on success
 */

export interface DraftStore<T> {
  /** Current draft, or null if nothing has been stored / it was cleared. */
  get: () => T | null;
  /** Replace the stored draft. */
  set: (value: T) => void;
  /** Drop the stored draft (e.g. on successful submit). */
  clear: () => void;
}

/**
 * Create an isolated in-memory draft store.
 * @param initial Optional starting value (defaults to null).
 */
export function createDraftStore<T>(initial: T | null = null): DraftStore<T> {
  let draft: T | null = initial;

  return {
    get: () => draft,
    set: (value: T) => {
      draft = value;
    },
    clear: () => {
      draft = null;
    },
  };
}
